import { Injectable } from '@angular/core';
import { AppInjector } from '../../app-injector';
import { MatDialog } from '@angular/material/dialog';
import { IntegrationItemServiceInf, StatusString } from '../interfaces/integrations';
import { SsoDialogComponent } from '../../modules/integrations-applications/components/integ-config-dialogs/sso-dialog/sso-dialog.component';
import { AppUserClaimsService } from './app-user-claims.service';
import { Util } from '../utils/Util';
import { AccountsService } from './accounts.service';
import { Account } from '@mitel/cloudlink-sdk';
import { AdminService } from 'src/app/core/services/admin.service';
import { AuthService } from './auth.service';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { ActionType, CategoryId } from '../utils/rbac.util';

@Injectable({
	providedIn: 'root'
})

// this service will hide whatever implementation we eventually have for getting the list of integrations
// types. Right now it is static
export class IntegSsoService implements IntegrationItemServiceInf {
	private appClaimsService: AppUserClaimsService = undefined;
	private accountsService: AccountsService = undefined; 
	private adminService: AdminService = undefined;
	private authService: AuthService = undefined;
	public dialog: MatDialog;
	private rbacAuthGuardService: RBACAuthGuardService;

	constructor(  ) 
	{
	}

	initServices()
	{
		if ( !this.appClaimsService )
			this.appClaimsService = AppInjector.injector.get(AppUserClaimsService);

		if ( !this.accountsService )
			this.accountsService = AppInjector.injector.get(AccountsService);

		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);

		if ( !this.authService )
			this.authService = AppInjector.injector.get(AuthService);

		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);

		if (!this.rbacAuthGuardService)
			this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);
	}
	
	getConfigurationDialog() : any
	{
		return SsoDialogComponent;
	}

	isSetupComplete(account: Account) : Promise<boolean>
	{
		// call new api which will return a 404 if it is not set up
		this.initServices();
		return this.authService.getSSODataForAccount(account.accountId).then( data => {
				// there is data so setup has been complete
				return true;
			})
			.catch( error => {
				// there is an error, but it might be a legit error
				if ( error && (
					(error.statusCode && error.statusCode == 404) ||
					(error.status && error.status == 404) ||
					(error.statusCode && error.statusCode == 400) ||
					(error.status && error.status == 400) ))
				{
					// this is the only error we will handle/ absorb
					return false;
				}
				else
				{
					throw(error);
				}
			});
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any>
	{
		if ( state == false )
		{
			this.initServices();
			console.log("deleting sso data for account", account);
			// errors to be handled by the calling component
			return this.authService.deleteSSODataForAccount( account.accountId ).then( retVal => {
				// second we will set the policy on the account
				// I know we have the account passes in, but it has been proven to be out of date, so 
				// we will request the latest from the accounts service
				let tempAccount = this.accountsService.getCurrentAccount();
				return this.adminService.setSsoPolicyForAccount( tempAccount, false ).then( newAccount => {
					this.accountsService.mergeAccountWithModel( newAccount );
					this.accountsService.setCurrentAccount( newAccount );
					return;
				});
			})
			.catch(err => {
				// its a delete so we don't care, we will just log it
				console.error("There was an error deleting sso data for an account, probably because it was never set up", err);
				return;
			});
		}
		else
			return; // nothing to implement yet
	}

	getStatusString(account: Account): Promise<StatusString>
	{
		return undefined;
	}

	getConfigButtonLabel(account: Account): Promise<string>
	{
		return undefined;
	}

	isButtonDisabled(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean>
	{
		return this.isSetupComplete(account).then(isSetup => {
			if ( isSetup === false )
			{
				return true; //there was nothing setup, so delete does not need to be confirmed
			}
			//display a dialog to so the user can confirm the deletion
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_SSO_CONFIRMATION } });
			return dialogRef.afterClosed().toPromise().then(result => {
				return result;
			});
		});
	}

	getSsoData( account: Account ): Promise<any>
	 {
		this.initServices();
		console.log("Getting sso data for the account", account);
		//using any casting because it is not available in the sdk yet
		// I know we have the account passes in, but it has been proven to be out of date, so 
		// we will request the latest from the accounts service

		let tempAccount = this.accountsService.getCurrentAccount() as any;
		let enabled = false;

		let data = {}
		// we have to determine if the data exists already or not
		return this.authService.getSSODataForAccount(account.accountId).then( ssodata => {
			
			// yes you are seeing right the policy in the accounts data is the opposite of the UI value
			if ( tempAccount.policy && tempAccount.policy.disableNativeUserLogin == false )
				enabled = true;

			// reformat the data
			// certs is an array. make it into a string
			let certs = ssodata.saml.certificates.join('\n');
			let theData: any = {
				idpEntityId: ssodata.saml.issuerId,
				entityId: ssodata.saml.serviceProviderIssuerId,
				replyUrl: ssodata.saml.serviceProviderReplyUrl,
				signInUrl: ssodata.saml.loginUrl,
				signingCert: certs,
				idpType: ssodata.type
			};
			Object.assign(data, theData, {ssoEnabled: enabled});
			return data;
		})
		.catch( error => {
			// there is an error, but it might be a legit error
			// testing for two sets of errors because not sure what is returned
			if ( error && (
				(error.statusCode && error.statusCode == 404) ||
				(error.status && error.status == 404) ))
			{
				// this is the only error we will handle/ absorb
				// here we will create some data to be used the first time
				let claims = this.appClaimsService.getClaims();
				let vals = Util.generateSsoValuesFor3rdParty(claims, account.accountId);

				Object.assign(data, vals, {ssoEnabled: enabled});
				return data;
			}
			else
			{
				// errors to be handled by the calling component
				throw(error);
			}
		});
	}

	setSsoData( account: Account, signInUrl: string, idpEntityId: string, signingCert: string,
		replyUrl: string, entityId: string, idpType: string, ssoEnabled: boolean ): Promise<boolean>
	{
		this.initServices();
		console.log("setting sso data for account", account);
		// errors to be handled by the calling component
		return this.authService.saveSSODataForAccount( account.accountId, entityId, replyUrl, 
			signInUrl, idpEntityId, signingCert, idpType ).then( retVal => {
			// second we will set the policy on the account
			// I know we have the account passes in, but it has been proven to be out of date, so 
			// we will request the latest from the accounts service
			let tempAccount = this.accountsService.getCurrentAccount();
			// yes you are seeing right the policy in the accounts data is the opposite of the UI value
			return this.adminService.setSsoPolicyForAccount( tempAccount, !ssoEnabled ).then( newAccount => {
				this.accountsService.mergeAccountWithModel( newAccount );
				this.accountsService.setCurrentAccount( newAccount );
				return true;
			});
		});
	}
	
	canBeSelected(account: Account): Promise<boolean>{
		this.initServices();

		let isDenied = this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.INTEGRATIONSSO, ActionType.VIEW);
		return Promise.resolve(!isDenied);
	}

}
